import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { AuthProvider, getCguardAccessToken } from '@cguard/auth';
import { configureCguardClient, createCguardQueryClient } from '@cguard/api-client/runtime';
import { isMockServerAllowed } from '@cguard/api-client/mocks/guard';
import { applyDirection, applyTheme } from '@cguard/design-system';
import { bootstrapI18n } from '@cguard/i18n';
import { router } from './router';
import { createVideoAuthConfig } from './lib/auth-config';
import { devVideoSession } from './lib/session';
import './styles/globals.css';

const queryClient = createCguardQueryClient();

configureCguardClient({
  baseUrl: '',
  getAccessToken: getCguardAccessToken,
});

function isMockDataEnabled(): boolean {
  if (!isMockServerAllowed()) return false;
  return (
    import.meta.env.VITE_DISABLE_MSW !== '1' &&
    (import.meta.env.DEV || document.documentElement.dataset.mocks === '1')
  );
}

async function bootstrap(): Promise<void> {
  applyTheme('dark');
  applyDirection('ltr', 'en');

  await bootstrapI18n({
    initialLocale: 'en',
    namespaces: ['common', 'video'],
    detectLanguage: false,
  });
  if (isMockDataEnabled()) {
    const { startMockServer } = await import('@cguard/api-client/mocks/browser');
    await startMockServer(undefined, devVideoSession);
  }

  const container = document.getElementById('app');
  if (!container) throw new Error('Missing #app root element');
  const fakeSession = isMockDataEnabled() ? devVideoSession : undefined;

  createRoot(container).render(
    <StrictMode>
      <AuthProvider
        config={createVideoAuthConfig({
          issuer: import.meta.env.VITE_KEYCLOAK_URL ?? 'https://kc.local/realms/cguard',
          clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'cguard-video-console',
          redirectUri: `${window.location.origin}/auth/callback`,
          transportAuthority: import.meta.env.VITE_KEYCLOAK_TRANSPORT_URL,
          browserOrigin: window.location.origin,
        })}
        {...(fakeSession ? { fakeSession } : {})}
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AuthProvider>
    </StrictMode>,
  );
}

void bootstrap();
